import React, { useState, useEffect } from 'react';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { Form, Input, Button, Card, Radio, message, Divider, Steps, Row, Col, Result } from 'antd';
import { theme } from '../../theme';

interface FormData {
  apellidoynombre: string;
  empresa: string;
  cuit_empresa: string;
  id_empresa: number;
  desmotadora: string;
}

interface Empresa {
  id_empresa: number;
  empresa: string;
}

const AltaUsuarios: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const email = user?.email;
  const verificado = user?.verificado;
  const [form] = Form.useForm<FormData>();
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState<number | null>(null);

  useEffect(() => {
    const fetchEmpresas = async () => {
      try {
        const token = await getAccessTokenSilently();
        const url = `${process.env.REACT_APP_USUARIOS_URL}/leer_empresas/`;
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
                
        if (!response.ok) {
          throw new Error(`Error al cargar las empresas: ${response.status}`);
        }
        
        const data = await response.json();
        setEmpresas(data);
      } catch (error) {
        message.error('Error al cargar las empresas');
      }
    };

    fetchEmpresas();
  }, [getAccessTokenSilently]);

  const handleEmpresaSelect = (empresaId: number) => {
    const empresaSeleccionada = empresas.find(emp => emp.id_empresa === empresaId);
    if (empresaSeleccionada) {
      setEmpresaSeleccionada(empresaId);
      form.setFieldsValue({
        id_empresa: empresaId,
        desmotadora: empresaSeleccionada.empresa
      });
    }
  };

  const validateCuit = (_: any, value: string) => {
    if (!value) {
      return Promise.reject('CUIT es requerido');
    }
    if (!/^\d{11}$/.test(value.replace(/-/g, ''))) {
      return Promise.reject('CUIT inválido (debe tener 11 dígitos)');
    }
    return Promise.resolve();
  };

  const onFinish = async (values: FormData) => {
    if (!empresaSeleccionada) {
      message.error('Por favor seleccione una empresa');
      return;
    }

    setIsLoading(true);
    message.loading('Enviando autorización...', 0);
    
    try {
      const token = await getAccessTokenSilently();
      const url = `${process.env.REACT_APP_USUARIOS_URL}/crear_usuarios/`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...values,
          email,
          password: '',
          id_empresa: empresaSeleccionada,
          desmotadora: values.desmotadora,
        })
      });

      if (!response.ok) {
        throw new Error('Error al crear el usuario');
      }

      const data = await response.json();
      message.destroy();
      message.success('Usuario creado exitosamente');
      window.location.reload();

    } catch (error) {
      console.error('Error:', error);
      message.destroy();
      message.error('Error al crear el usuario. Por favor, intente nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  // Add this new function to determine current step
  const getCurrentStep = () => {
    if (!user) return 0; // Not logged in
    if (verificado === 0) return 2; // Waiting for authorization
    if (verificado === 1) return 3; // Done
    return 1; // Form completion step
  };

  return (
    <>
      <Divider orientation="left">
        <h1 style={{color: theme.colors.primaryDark}}>Alta de usuarios</h1>
      </Divider>      
      <Steps
        current={getCurrentStep()}
        items={[
          {
            title: 'Login',
            description: 'Iniciar sesión',
          },
          {
            title: 'Completar Formulario',
            description: 'Ingresar datos',
          },
          {
            title: 'Autorización',
            description: 'Esperar validación',
          },
          {
            title: 'Finalizado',
            description: 'Usuario activo',
          },
        ]}
        style={{ 
          maxWidth: '90%', 
          margin: '2rem auto',
          padding: '0 1rem'
        }}
      />
      
      {verificado === 0 ? (
          <Result
            status="success"
            title="El usuario fue creado exitosamente"
            subTitle="Esperando validación por parte de la Desmotadora"
            style={{ 
              backgroundColor: theme.colors.backgroundGray,
              borderRadius: '8px',
              padding: '2rem', 
              marginTop: '5rem', 
              width: '50%', 
              marginLeft: '25%',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)' 
            }}
          />
      ) : (
        <Card 
          style={{ 
            maxWidth: 600, 
            margin: '0 auto', 
            padding: '1rem',
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
          }}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            disabled={isLoading}
            size="small"
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="apellidoynombre"
                  label="Nombre de usuario"
                  rules={[{ required: true, message: 'Apellido y Nombre es requerido' }]}
                  style={{ marginBottom: '0.75rem' }}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="empresa"
                  label="Razón social"
                  rules={[{ required: true, message: 'Empresa es requerida' }]}
                  style={{ marginBottom: '0.75rem' }}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="cuit_empresa"
              label="CUIT Empresa"
              rules={[{ validator: validateCuit, required: true, message: 'CUIT es requerido' }]}
              style={{ marginBottom: '0.75rem' }}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="desmotadora"
              hidden={true}
            >
              <Input />
            </Form.Item>

            <Card 
              title="Seleccione la Desmotadora de algodón" 
              style={{
                marginTop: '1rem',
                backgroundColor: theme.colors.backgroundGray,
                borderColor: theme.colors.borderLight
              }}
              size="small"
              styles={{
                header: {
                  color: 'white',
                  borderBottom: `1px solid ${theme.colors.borderLight}`,
                  fontSize: '14px',
                  fontWeight: 500
                }
              }}
            >
              <Radio.Group 
                value={empresaSeleccionada} 
                onChange={(e) => handleEmpresaSelect(e.target.value)}
                style={{ width: '100%' }}
              >
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(2, 1fr)', 
                  gap: '0.5rem',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  backgroundColor: theme.colors.backgroundGray
                }}>
                  {empresas.map((empresa) => (
                    <Radio 
                      key={empresa.id_empresa} 
                      value={empresa.id_empresa}
                      style={{ 
                        marginRight: 0,
                        padding: '0.25rem 0',
                        backgroundColor: theme.colors.backgroundGray
                      }}
                    >
                      {empresa.empresa}
                    </Radio>
                  ))}
                </div>
              </Radio.Group>
            </Card>

            <Form.Item style={{ textAlign: 'center', marginTop: '1rem', marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                disabled={!empresaSeleccionada}
                size="small"
                style={{ width: 150 }}
              >
                {isLoading ? 'Enviando...' : 'Enviar'}
              </Button>
            </Form.Item>
          </Form>
        </Card>
      )}
    </>
  );
};

export default withAuthenticationRequired(AltaUsuarios);
